import { BackgroundTaskBase } from "@/utils/background-tasks";
import {
  BackgroundTaskState,
  ProgressApiSupportedTaskTypes,
} from "@faro-lotv/service-wires";

/**
 * Returns a human-readable description of the task type
 *
 * @param task - The background task
 * @returns A string describing the task type (e.g., "AI asset detection", "Floor plan generation")
 */
export function getTaskTypeDescription(task: BackgroundTaskBase): string {
  switch (task.type) {
    // Client-side tasks
    case "ManualBlurring":
      return "Manual blurring";
    case "MultiFileUpload":
      return "Import multiple files";
    case "FileUpload":
      return "Import";
    case "TiledImageSheetGeneration":
      return "Tiled image sheet generation";
    case "IpxOrder":
      return "Scan-to-BIM order";

    // Processing tasks
    case ProgressApiSupportedTaskTypes.assetDetection:
      return "AI asset detection";
    case ProgressApiSupportedTaskTypes.pointCloudToPotree:
    case ProgressApiSupportedTaskTypes.pointCloudE57ToLaz:
    case ProgressApiSupportedTaskTypes.pointCloudGSToLaz:
      return "Point cloud processing";
    case ProgressApiSupportedTaskTypes.bimModelImport:
      return "BIM model import";
    case ProgressApiSupportedTaskTypes.videoMode:
      return "Video mode";
    case ProgressApiSupportedTaskTypes.sceneConversion:
      return "Scene conversion";
    case ProgressApiSupportedTaskTypes.pointCloudLazToPotree:
      return "Scan processing";
    case ProgressApiSupportedTaskTypes.vlmIndexMigration:
      return "";

    // Export tasks
    case ProgressApiSupportedTaskTypes.pointCloudExport:
    case ProgressApiSupportedTaskTypes.structuredExport:
      return "Point cloud export";
    case ProgressApiSupportedTaskTypes.orthophotoGeneration:
      return "Orthophoto";
    case ProgressApiSupportedTaskTypes.captur3dFloorplanGeneration:
      return "Floor plan generation";

    // Registration tasks
    case ProgressApiSupportedTaskTypes.c2cRegistration:
    case ProgressApiSupportedTaskTypes.registerMultiCloudDataSet:
    case ProgressApiSupportedTaskTypes.mergePointClouds:
      return "Registration";

    default:
      return "Processing";
  }
}

/**
 * Returns the status label for a task based on its type and state
 *
 * @param task - The background task
 * @returns A string describing the current status (e.g., "Processing", "Uploading", "Completed")
 */
export function getTaskStatusLabel(task: BackgroundTaskBase): string {
  // Handle completed/failed/cancelled states first
  switch (task.state) {
    case BackgroundTaskState.succeeded:
      return "Completed";
    case BackgroundTaskState.failed:
      return "Failed";
    case BackgroundTaskState.aborted:
      return "Cancelled";
  }

  if (task.progress === 0) {
    switch (task.state) {
      case BackgroundTaskState.started:
        return "Starting";
      case BackgroundTaskState.created:
        return "Created";
      case BackgroundTaskState.scheduled:
        return "Scheduled";
    }
  }

  // For active states, return status based on task type
  switch (task.type) {
    case "FileUpload":
      return "Uploading";

    case "TiledImageSheetGeneration":
      return "Importing";

    case ProgressApiSupportedTaskTypes.c2cRegistration:
    case ProgressApiSupportedTaskTypes.registerMultiCloudDataSet:
    case ProgressApiSupportedTaskTypes.mergePointClouds:
      return "Registering";

    case ProgressApiSupportedTaskTypes.pointCloudExport:
    case ProgressApiSupportedTaskTypes.structuredExport:
    case ProgressApiSupportedTaskTypes.orthophotoGeneration:
    case ProgressApiSupportedTaskTypes.captur3dFloorplanGeneration:
      return "Exporting";

    case ProgressApiSupportedTaskTypes.assetDetection:
    case ProgressApiSupportedTaskTypes.bimModelImport:
    case ProgressApiSupportedTaskTypes.videoMode:
    case ProgressApiSupportedTaskTypes.sceneConversion:
    case ProgressApiSupportedTaskTypes.pointCloudLazToPotree:
    case ProgressApiSupportedTaskTypes.pointCloudE57ToLaz:
    case ProgressApiSupportedTaskTypes.pointCloudGSToLaz:
    default:
      return "Processing";
  }
}

/**
 * Checks if a task is in an active state (not completed, failed, or cancelled)
 *
 * @param state - The background task state
 * @returns true if the task is active
 */
export function isTaskActive(state: BackgroundTaskState): boolean {
  return (
    state === BackgroundTaskState.created ||
    state === BackgroundTaskState.scheduled ||
    state === BackgroundTaskState.started
  );
}
